import { useEffect, useMemo, useState, type ImgHTMLAttributes } from "react";
import {
  appStorageKeyFromUrl,
} from "@shared/storageContract";
import {
  isResponsiveImageManifest,
  responsiveImageSrcSet,
  responsiveImageSrcSetFromManifest,
  responsiveManifestStorageKey,
  withOriginalOrigin,
} from "@shared/responsiveImages";

const TRANSPARENT_PIXEL = "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=";
const manifestRequests = new Map<string, Promise<unknown>>();

type ResponsiveImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "srcSet"> & {
  src: string;
  /**
   * Largest derivative this rendered view can use. This is a display ceiling,
   * not an instruction to enlarge the source.
   */
  maxWidth?: number;
};

/**
 * Shared responsive image contract for local media and public App Storage
 * rasters. External images and excluded formats render as ordinary images.
 */
export default function ResponsiveImage({
  src,
  maxWidth = 1600,
  sizes = "100vw",
  loading = "lazy",
  decoding = "async",
  onError,
  ...props
}: ResponsiveImageProps) {
  const sourceKey = appStorageKeyFromUrl(src);
  const manifestKey = sourceKey ? responsiveManifestStorageKey(sourceKey) : null;
  const isManagedSource = Boolean(sourceKey && manifestKey);
  const resolutionKey = `${src}:${maxWidth}`;
  const [fallbackKey, setFallbackKey] = useState<string | null>(null);
  const [storageResolution, setStorageResolution] = useState<{
    key: string;
    srcSet?: string;
  } | null>(null);
  const useFallback = fallbackKey === resolutionKey;
  const activeStorageResolution = storageResolution?.key === resolutionKey
    ? storageResolution
    : null;
  useEffect(() => {
    if (!sourceKey || !manifestKey) {
      return;
    }
    if (typeof fetch !== "function") {
      setStorageResolution({ key: resolutionKey });
      return;
    }
    let cancelled = false;
    const manifestUrl = withOriginalOrigin(src, `/app-storage/${manifestKey}`);
    const request = manifestRequests.get(manifestUrl)
      ?? fetch(manifestUrl, { cache: "force-cache" })
        .then(response => response.ok ? response.json() : null)
        .catch(() => null);
    manifestRequests.set(manifestUrl, request);
    void request
      .then(manifest => {
        if (!cancelled && isResponsiveImageManifest(manifest, sourceKey)) {
          setStorageResolution({
            key: resolutionKey,
            srcSet: responsiveImageSrcSetFromManifest(src, manifest, maxWidth),
          });
        } else if (!isResponsiveImageManifest(manifest, sourceKey)) {
          manifestRequests.delete(manifestUrl);
          if (!cancelled) setStorageResolution({ key: resolutionKey });
        }
      })
      .catch(() => {
        manifestRequests.delete(manifestUrl);
        if (!cancelled) setStorageResolution({ key: resolutionKey });
      });
    return () => { cancelled = true; };
  }, [src, maxWidth, sourceKey, manifestKey, resolutionKey]);
  const srcSet = useMemo(
    () => useFallback
      ? undefined
      : activeStorageResolution?.srcSet ?? responsiveImageSrcSet(src, maxWidth),
    [src, maxWidth, activeStorageResolution, useFallback],
  );
  const displayedSrc = isManagedSource && !activeStorageResolution && !useFallback
    ? TRANSPARENT_PIXEL
    : src;

  return (
    <img
      {...props}
      src={displayedSrc}
      srcSet={srcSet}
      sizes={srcSet ? sizes : undefined}
      loading={loading}
      decoding={decoding}
      onError={event => {
        if (srcSet && !useFallback) {
          event.currentTarget.srcset = "";
          event.currentTarget.src = src;
          setFallbackKey(resolutionKey);
          return;
        }
        onError?.(event);
      }}
    />
  );
}